import { syncPreorderCart } from '@theme/preorder';
import { ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * Keeps the preorder discount code and cart attribute in sync with the cart.
 *
 * Runs on every page (home, PDP, and the rest) and again after cart add / remove /
 * quantity / discount changes. Both the code and `_preorder` attribute are applied
 * only when every line is a preorder.
 */
class PreorderCartGuard extends HTMLElement {
  /** @type {boolean} */
  #syncInProgress = false;

  /** @type {number} */
  #syncTimer = 0;

  /** @type {(event: Event) => void} */
  #onCartUpdate = (event) => {
    const detail = /** @type {{detail?: {data?: {didError?: boolean, source?: string}}}} */ (event).detail;
    if (detail?.data?.didError) return;
    if (detail?.data?.source === 'preorder-cart-guard') return;
    this.#scheduleSync(false);
  };

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#scheduleSync(true);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    window.clearTimeout(this.#syncTimer);
  }

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#scheduleSync(true);
  };

  /**
   * @param {boolean} trustLiquidState
   */
  #scheduleSync(trustLiquidState) {
    window.clearTimeout(this.#syncTimer);
    this.#syncTimer = window.setTimeout(() => {
      this.#sync(trustLiquidState);
    }, trustLiquidState ? 0 : 150);
  }

  /**
   * @param {boolean} trustLiquidState
   */
  async #sync(trustLiquidState) {
    if (this.#syncInProgress) {
      this.#scheduleSync(trustLiquidState);
      return;
    }

    const discountCode = this.dataset.preorderDiscountCode?.trim();
    const attributeName = this.dataset.preorderAttributeName?.trim();
    const attributeValue = this.dataset.preorderAttributeValue ?? '';
    const liquidState = this.dataset.preorderState;

    if (!discountCode && !attributeName) return;

    this.#syncInProgress = true;

    try {
      const sectionIds = [
        ...new Set(
          [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
            .filter((sectionId) => Boolean(sectionId))
        ),
      ];

      const result = await syncPreorderCart({
        discountCode,
        attributeName,
        attributeValue,
        liquidState,
        trustLiquidState,
        sectionIds,
      });

      if (!result?.sections) return;

      for (const sectionId of sectionIds) {
        const html = result.sections[sectionId];
        if (html) morphSection(sectionId, html);
      }
    } finally {
      this.#syncInProgress = false;
    }
  }
}

if (!customElements.get('preorder-cart-guard')) {
  customElements.define('preorder-cart-guard', PreorderCartGuard);
}
